import React, { useState, useEffect, useRef } from 'react';

const PHRASES = [
  'Sleek. Seamless. Yours.',
  'Where Artistry Meets Architecture.',
  'Seamless Living, Inside and Out.',
  'Bespoke Resin. Polished Perfection. Ultimate Transformation.',
  'The Marble Look, The Modern Touch.',
];

// Character index where gold part starts for each phrase
const GOLD_START = [16, 21, 22, 35, 17];

const CHAR_DELAY_MS = 85;
const DELETE_DELAY_MS = 55;
const HOLD_MS = 5000;
const CURSOR_BLINK_MS = 530;

function Hero() {
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [visibleLength, setVisibleLength] = useState(0);
  const [showCursor, setShowCursor] = useState(true);
  const [phase, setPhase] = useState('typing'); // 'typing' | 'hold' | 'deleting'
  const holdTimeoutRef = useRef(null);

  const phrase = PHRASES[phraseIndex];
  const goldStart = GOLD_START[phraseIndex];
  const typingComplete = visibleLength >= phrase.length;

  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      setVisibleLength(PHRASES[0].length);
      setShowCursor(false);
      setPhase('hold');
    }
  }, []);

  useEffect(() => {
    if (phase !== 'typing' || visibleLength >= phrase.length) return;
    const t = setTimeout(() => setVisibleLength((n) => n + 1), CHAR_DELAY_MS);
    return () => clearTimeout(t);
  }, [phase, visibleLength, phrase.length]);

  useEffect(() => {
    if (phase !== 'typing' || !typingComplete) return;
    holdTimeoutRef.current = setTimeout(() => setPhase('deleting'), HOLD_MS);
    return () => {
      if (holdTimeoutRef.current) clearTimeout(holdTimeoutRef.current);
    };
  }, [phase, typingComplete]);

  useEffect(() => {
    if (phase !== 'deleting' || visibleLength <= 0) return;
    const t = setTimeout(() => setVisibleLength((n) => n - 1), DELETE_DELAY_MS);
    return () => clearTimeout(t);
  }, [phase, visibleLength]);

  useEffect(() => {
    if (phase !== 'deleting' || visibleLength > 0) return;
    setPhraseIndex((i) => (i + 1) % PHRASES.length);
    setPhase('typing');
  }, [phase, visibleLength]);

  useEffect(() => {
    if (phase === 'hold') {
      const hide = setTimeout(() => setShowCursor(false), 400);
      return () => clearTimeout(hide);
    }
    setShowCursor(true);
  }, [phase]);

  useEffect(() => {
    if (!showCursor || phase === 'hold') return;
    const id = setInterval(() => setShowCursor((s) => !s), CURSOR_BLINK_MS);
    return () => clearInterval(id);
  }, [showCursor, phase]);

  const part1 = phrase.slice(0, Math.min(visibleLength, goldStart));
  const part2 = visibleLength > goldStart ? phrase.slice(goldStart, visibleLength) : '';

  return (
    <section id="hero" className="hero">
      <div
        className="hero-bg"
        style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/images/hero-background.png)` }}
      />
      <div className="hero-shine" />
      <div className="hero-content">
        <h1 className="hero-title hero-title-typewriter">
          <span className="hero-title-emerald">{part1}</span>
          {part2 && <span className="hero-title-gold">{part2}</span>}
          {showCursor && <span className="hero-title-cursor" aria-hidden="true">|</span>}
        </h1>
        <p className="hero-tagline">
          Imagine the opulence of marble without the maintenance. Imagine a floor that flows seamlessly from your kitchen to your patio. At Emerald Horizon, we are creators of atmosphere.
        </p>
        <a href="#services" className="hero-cta" onClick={(e) => { e.preventDefault(); document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' }); }}>
          <span className="hero-cta-text">Don't just renovate. Transform.</span>
        </a>
      </div>
    </section>
  );
}

export default Hero;
